import { useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Chip,
  Divider,
  Button,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { format, parseISO } from 'date-fns';
import { PermissionGate, Permissions } from '@billda/permissions-ui';
import { LabelValueRow } from '../LabelValueRow';
import { formatCurrency } from '../../../../shared/utils/currency';
import { useEditAdminDeduction } from '../../hooks/useEditAdminDeduction';
import type { AdminDeductionRow } from '@billda/api';
import { CancelDeductionDialog } from './CancelDeductionDialog';

type PanelView = 'details' | 'edit';

const NOTES_MAX_LENGTH = 1000;

interface DeductionDetailPanelContentProps {
  deduction: AdminDeductionRow;
  onSuccess: (message: string) => void;
}

const statusChipConfig: Record<
  string,
  { color: 'success' | 'warning' | 'error' | 'default'; label: string }
> = {
  Approved: { color: 'success', label: 'Approved' },
  Booked: { color: 'warning', label: 'Booked' },
  Rejected: { color: 'error', label: 'Rejected' },
  Cancelled: { color: 'default', label: 'Cancelled' },
};

function DetailsView({
  deduction,
  onEdit,
  onCancel,
}: {
  deduction: AdminDeductionRow;
  onEdit: () => void;
  onCancel: () => void;
}) {
  const chipConfig = statusChipConfig[deduction.status] ?? {
    color: 'default' as const,
    label: deduction.status,
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Stack spacing={2}>
          {/* Description + Amount header */}
          <Box>
            <Typography variant="body1" fontWeight={600}>
              {deduction.description}
            </Typography>
            <Typography
              variant="h5"
              color="error.main"
              fontWeight={600}
              sx={{ mt: 0.5 }}
            >
              {formatCurrency(deduction.amount)}
            </Typography>
          </Box>

          <Divider />

          {/* Properties */}
          <LabelValueRow
            label="Reference"
            value={deduction.uniqueReference ?? '—'}
          />
          <LabelValueRow
            label="Status"
            value={
              <Chip
                label={chipConfig.label}
                color={chipConfig.color}
                size="small"
                sx={{ fontWeight: 600 }}
              />
            }
          />
          <LabelValueRow
            label="Worker / Gang"
            value={deduction.workerOrGangName ?? '—'}
          />
          <LabelValueRow label="Site" value={deduction.siteName ?? '—'} />
          <LabelValueRow label="Plot" value={deduction.plotReference ?? '—'} />
          <LabelValueRow
            label="Date"
            value={
              deduction.deductionDate
                ? format(parseISO(deduction.deductionDate), 'dd MMM yyyy')
                : '—'
            }
          />

          <Divider />

          <LabelValueRow
            label="Created by"
            value={deduction.createdByName ?? '—'}
          />
          <LabelValueRow
            label="Created"
            value={format(parseISO(deduction.createdAt), 'dd MMM yyyy')}
          />

          {deduction.notes && (
            <>
              <Divider />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.5 }}
                >
                  Notes
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {deduction.notes}
                </Typography>
              </Box>
            </>
          )}

          {/* Cancellation info for cancelled deductions */}
          {deduction.status === 'Cancelled' && deduction.cancellationNotes && (
            <>
              <Divider />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.5 }}
                >
                  Cancellation Notes
                </Typography>
                <Typography variant="body2">
                  {deduction.cancellationNotes}
                </Typography>
              </Box>
            </>
          )}
        </Stack>
      </Box>

      {/* Fixed footer with actions */}
      {(deduction.canEdit || deduction.canCancel) && (
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <Stack direction="row" spacing={2}>
            {deduction.canEdit && (
              <PermissionGate permission={Permissions.Deductions.Approve}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={onEdit}
                  fullWidth
                >
                  Edit
                </Button>
              </PermissionGate>
            )}
            {deduction.canCancel && (
              <PermissionGate permission={Permissions.Deductions.Approve}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={onCancel}
                  fullWidth
                  data-testid="cancel-deduction-button"
                >
                  Cancel Deduction
                </Button>
              </PermissionGate>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

function EditView({
  deduction,
  onBack,
  onSuccess,
}: {
  deduction: AdminDeductionRow;
  onBack: () => void;
  onSuccess: (message: string) => void;
}) {
  const [description, setDescription] = useState(deduction.description);
  const [amount, setAmount] = useState(deduction.amount.toString());
  const [notes, setNotes] = useState(deduction.notes ?? '');

  const { mutate: editDeduction, isPending } = useEditAdminDeduction();

  const parsedAmount = parseFloat(amount);
  const amountNum = isNaN(parsedAmount) ? 0 : parsedAmount;
  const isValid =
    description.trim().length >= 3 &&
    amountNum > 0 &&
    notes.length <= NOTES_MAX_LENGTH;

  const handleSave = () => {
    editDeduction(
      {
        deductionId: deduction.id,
        request: {
          description: description.trim(),
          amount: amountNum,
          notes: notes.trim() || null,
        },
      },
      {
        onSuccess: () => onSuccess('Deduction updated successfully'),
      }
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Stack spacing={2}>
          {/* Reference (read-only) */}
          <Typography variant="body2" color="text.secondary">
            {deduction.uniqueReference ?? '—'}
          </Typography>

          {/* Editable fields */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Deduction
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Name"
                  required
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  inputProps={{ maxLength: 200 }}
                  helperText={`${description.length}/200`}
                />
                <TextField
                  label="Amount"
                  required
                  type="number"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  inputProps={{ min: 0, step: '0.01' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">£</InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Notes"
                  multiline
                  minRows={3}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  inputProps={{ maxLength: NOTES_MAX_LENGTH }}
                  placeholder="Office only — this doesn't show in the app"
                  helperText={`${notes.length}/${NOTES_MAX_LENGTH}`}
                />
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>

      {/* Fixed footer */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            disabled={isPending}
            fullWidth
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!isValid || isPending}
            fullWidth
            startIcon={
              isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : undefined
            }
          >
            {isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}

export function DeductionDetailPanelContent({
  deduction,
  onSuccess,
}: DeductionDetailPanelContentProps) {
  const [panelView, setPanelView] = useState<PanelView>('details');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  if (panelView === 'edit') {
    return (
      <EditView
        deduction={deduction}
        onBack={() => setPanelView('details')}
        onSuccess={onSuccess}
      />
    );
  }

  return (
    <>
      <DetailsView
        deduction={deduction}
        onEdit={() => setPanelView('edit')}
        onCancel={() => setCancelDialogOpen(true)}
      />
      <CancelDeductionDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        deduction={{
          id: deduction.id,
          uniqueReference: deduction.uniqueReference,
          description: deduction.description,
          amount: deduction.amount,
          workerOrGangName: deduction.workerOrGangName,
          status: deduction.status,
        }}
        onSuccess={onSuccess}
      />
    </>
  );
}
